<template>
    <!-- Choose which students belong to a group (or which groups belong to an instructor).
         The ticked list REPLACES the current one when the window closes. Closing it any way
         (Save, ×, Esc, clicking outside) saves, as it always has; only Cancel discards. -->
    <EditWindow v-if="data.show_addfromlist_window" :heading="heading" :name="data.parent.ui" :width="560"
                @close="close_addfromlist_window()">

        <div class="afl-search">
            <span class="material-symbols-outlined" aria-hidden="true">search</span>
            <input v-model="query" type="search" :placeholder="`Search ${plural}…`" autocomplete="off" aria-label="Search">
        </div>

        <div class="afl-filters">
            <span class="afl-count">{{ selected_count }} selected</span>
            <label class="afl-only">
                <input type="checkbox" v-model="only_selected"> Show selected only
            </label>
        </div>

        <ul class="afl-list">
            <li v-for="child in visible_children" :key="child.id">
                <label class="afl-item" :class="{selected:child.selected}">
                    <input type="checkbox" :checked="!!child.selected" @change="toggle_select_child(child)">
                    <span class="afl-name">{{ child.ui }}</span>
                </label>
            </li>
        </ul>
        <p v-if="!visible_children.length" class="afl-empty">
            {{ query ? `No ${plural} match “${query}”.` : `No ${plural} to show.` }}
        </p>

        <template #footer="{close}">
            <button type="button" class="afl-btn" @click.stop="cancelled=true; close()">Cancel</button>
            <button type="button" class="afl-btn afl-btn-primary" @click.stop="close()">Save</button>
        </template>
    </EditWindow>
</template>

<script setup>

import {inject,ref,computed,watch} from 'vue'
import axios from 'axios'
import EditWindow from '@/components/edit/EditWindow.vue'

let data=inject('data')

let selected=[]
let query=ref('')
let only_selected=ref(false)
let cancelled=ref(false)

const plural=computed(()=>((data.children_endpoint||'item').toLowerCase())+'s')
const heading=computed(()=>`Choose ${plural.value} for ${(data.parent && data.parent.endpoint || '').toLowerCase()}`)

const children_list=computed(()=>Array.isArray(data.children) ? data.children : Object.values(data.children||{}))
const selected_count=computed(()=>children_list.value.filter(child=>child.selected).length)

// Accents and case don't matter: "jose" finds "José".
function fold(text){
    return String(text||'').normalize('NFD').replace(/[̀-ͯ]/g,'').toLowerCase()
}
const visible_children=computed(()=>{
    const q=fold(query.value.trim())
    return children_list.value.filter(child=>
        (!only_selected.value || child.selected) && (!q || fold(child.ui).includes(q))
    )
})

watch(()=>data.show_addfromlist_window,open=>{
    if(open){
        query.value=''
        only_selected.value=false
        cancelled.value=false
    }
})

async function close_addfromlist_window(){

        if(!cancelled.value){
            selected=children_list.value.filter(child=>child.selected).map(child=>child.id)

            let response=await axios.post('/admin/addfromlist/',{parent:data.parent,children_endpoint:data.children_endpoint,children_ids:selected})
            Object.assign(data.parent,response.data)
        }

        data.hide_addfromlist_window=true
        data.show_addfromlist_window=false
        data.children={}
    }

function toggle_select_child(child){

    if (child.selected){
        child.selected=false
    }else{
        child.selected=true
        selected.push(child.id)
    }

}

</script>

<style scoped>

.afl-search{
    position:sticky;
    top:0;
    z-index:1;
    display:flex;
    align-items:center;
    gap:8px;
    padding:0 12px;
    background:#ffffff;
    border:1px solid var(--ew-field-line);
    border-radius:10px;
}
.afl-search:focus-within{
    outline:2px solid var(--ew-focus);
    outline-offset:-1px;
}
.afl-search .material-symbols-outlined{ color:var(--ew-muted); }
.afl-search input{
    flex:1;
    min-width:0;
    height:44px;
    font-size:16px;     /* 16px stops iOS zooming in on focus */
    color:var(--ew-text);
    background:transparent;
    border:none;
    outline:none;
}

.afl-filters{
    display:flex;
    flex-wrap:wrap;
    align-items:center;
    justify-content:space-between;
    gap:8px;
    font-size:13px;
    color:var(--ew-muted);
}
.afl-only{
    display:flex;
    align-items:center;
    gap:6px;
    cursor:pointer;
}

.afl-list{
    display:flex;
    flex-direction:column;
    margin:0;
    padding:0;
    list-style:none;
    background:var(--ew-card);
    border:1px solid var(--ew-line);
    border-radius:10px;
}
.afl-list li + li{ border-top:1px solid var(--ew-line); }
.afl-item{
    display:flex;
    align-items:center;
    gap:12px;
    min-height:48px;
    padding:6px 14px;
    cursor:pointer;
}
.afl-item:hover{ background:rgba(6,115,158,0.06); }
.afl-item.selected{ background:rgba(42,138,110,0.10); }
.afl-item input{
    flex:none;
    width:20px;
    height:20px;
    accent-color:var(--ew-ok);
}
.afl-name{ overflow-wrap:anywhere; }

.afl-empty{
    margin:0;
    color:var(--ew-muted);
    font-style:italic;
}

.afl-btn{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    min-width:96px;
    height:42px;
    min-height:42px;
    padding:0 18px;
    font-size:15px;
    font-weight:600;
    word-break:normal;
    color:var(--ew-text);
    background:#ffffff;
    border:1px solid var(--ew-field-line);
    border-radius:10px;
    box-shadow:none;
}
.afl-btn:hover{
    color:var(--ew-text);
    background:var(--ew-bg);
    box-shadow:none;
}
.afl-btn-primary{
    color:#ffffff;
    background:var(--ew-focus);
    border-color:var(--ew-focus);
}
.afl-btn-primary:hover{
    color:#ffffff;
    background:#055a7c;
}

@media (max-width:600px){
    .afl-btn{ flex:1 1 0; }
}

</style>
